import { AxiosError, AxiosResponse } from "axios";
import { Dispatch } from "react";
import { AnyAction } from "redux";
import { isNull, isUndefined } from "underscore";
import { GetVehicleAssignmentsQuery } from "../../api/parkingProducts.api";
import { FmpFilters } from "../../constants/filtering.constants";
import { GetBulkImportProgressResponseModel } from "../../models/bulkImport/GetBulkImportProgressResponseModel";
import { GetBulkImportStatusResponseModel } from "../../models/bulkImport/GetBulkImportStatusResponseModel";
import { StartBulkInvitationsRequestModel } from "../../models/bulkImport/StartBulkInvitationsRequestModel";
import { ValidateDelegeesRequestModel } from "../../models/bulkImport/ValidateDelegeesRequestModel";
import { ValidateDelegeesResponseModel } from "../../models/bulkImport/ValidateDelegeesResponseModel";
import {
  DelegatedParkingRight,
  DelegatedParkingRightsWithPagination,
  DelegeeWithProductsModel,
  VehicleAssignment,
  VehicleAssignmentModel
} from "../../models/delegations/DelegatedParkingProductsModel";
import { DeleteDelegationsRequestModel } from "../../models/delegations/DeleteDelegationsRequestModel";
import { ErrorDetailsModel } from "../../models/ErrorDetailsModel";
import { WithFiltersAndPaging } from "../../models/filters/WithFiltersAndPaging";
import { ForFleetManager } from "../../models/ForFleetManager";
import { LocatedParkingProduct } from "../../models/LocatedParkingProduct";
import { ParkingProductModel, ResendDelegeeInviteRequest } from "../../models/ParkingProductModel";
import { RevokeParkingRightsModel } from "../../models/RevokeParkingRightsModel";
import { AssignedVehicleSuccessfulMutation } from "../../models/vehicle-assignments/AssignedVehicleSuccessfulMutation";
import { AssignVehicleRequestModel } from "../../models/vehicle-assignments/AssignVehicleRequestModel";
import { CalculatedWhitelistDeltaResponse, CalculateWhitelistDeltaRequest } from "../../models/vehicle-assignments/CalculateWhitelistDelta";
import { DeleteVehicleAssignmentsModel } from "../../models/vehicle-assignments/DeleteVehicleAssignmentsModel";
import { GetBulkVehicleAssignmentsStatusResponseModel } from "../../models/vehicle-assignments/GetBulkVehicleAssignmentsStatusResponseModel";
import { GetBulkVehicleImportProgressResponseModel } from "../../models/vehicle-assignments/GetBulkVehicleImportProgressResponseModel";
import { RevokeVehicleAssignmentsModel } from "../../models/vehicle-assignments/RevokeVehicleAssignmentsModel";
import { StartBulkVehicleAssignmentRequest } from "../../models/vehicle-assignments/StartBulkVehicleAssignmentRequest";
import { UpdateVehicleRequestModel } from "../../models/vehicle-assignments/UpdateVehicleRequestModel";
import { InsufficientAvailabilityDetail, ValidatedVehicleBulkUploadResponse, ValidateVehicleBulkUploadRequest } from "../../models/vehicle-assignments/ValidateVehicleBulkUpload";
import parkingProductsService, { convertFiltersToGetVehicleAssignmentsQuery } from "../../services/parkingProducts.service";
import { toastTriggers } from "../../ui/CustomToast/toastTriggers";
import { setTodayActivationDatesToNowIfToday } from "../../utils/activationDate.utils";
import { parkingProductsTypes, strictParkingProductsTypes } from "../action-types/parkingProducts.types";
import { Action, AsyncAction, failure, request, success } from "../helpers/action.helper";
import { RootReducer } from "../reducers";
import { loaderActions } from "./loader.actions";
import { checkForCrmIdOrLinkedAccessDevice } from "./parkingProducts.toast";
import { checkForSeasonTicketCrmIdNotValidError } from "../../utils";

type StrictParkingProductsAction =
  | {
      type: typeof strictParkingProductsTypes.SET_MY_FLEET_VEHICLES_TOTAL_RECORDS;
      payload: { totalRecords: number };
    }
  | AsyncAction<
      typeof strictParkingProductsTypes.GET_BULK_VEHICLE_IMPORT_PROGRESS,
      {
        success: {
          progress: number;
          errorCount: number;
          warningCount: number;
        };
      }
    >
  | AsyncAction<
      typeof strictParkingProductsTypes.VALIDATE_VEHICLE_BULK_UPLOAD,
      {
        success: {
          amountOfVehiclesToCreate: number;
          amountOfExceedingVehicles: number;
          fileReference: string;
          insufficientAvailabilityDetails?: InsufficientAvailabilityDetail[];
        };
      }
    >
  | AsyncAction<
      typeof strictParkingProductsTypes.GET_BULK_VEHICLE_ASSIGNMENTS_STATUS,
      {
        success: {
          isCompleted: boolean;
        };
      }
    >
  | AsyncAction<
      typeof strictParkingProductsTypes.CALCULATE_WHITELIST_DELTA,
      {
        success: {
          amountOfVehiclesToUpdate: number;
          amountOfVehiclesToCreate: number;
          amountOfVehiclesToDelete: number;
          amountOfVehiclesWithNoChanges: number;
          hasChanges: boolean;
          fileReference: string;
        };
      }
    >
  | Action<typeof strictParkingProductsTypes.RESET_BULK_VEHICLE_IMPORT_PROGRESS>
  | AsyncAction<typeof strictParkingProductsTypes.START_BULK_VEHICLE_ASSIGNMENT>;

type AllowSuffix<T extends string> = T | `${T}_REQUEST` | `${T}_SUCCESS` | `${T}_FAILURE`;

type TheRestOfTypes = AllowSuffix<(typeof parkingProductsTypes)[keyof typeof parkingProductsTypes]>;

export type ParkingProductsActionType = {
  type: TheRestOfTypes;
  parkingProductsPayload?: ParkingProductModel[];
  locatedParkingProductsPayload?: LocatedParkingProduct[];
  delegatedParkingProductsPayload?: {
    pageNumber: number;
    totalPages?: number;
    totalRecords?: number;
    payload?: DelegatedParkingRight[];
  };
  delegeeDetailsPayload?: DelegeeWithProductsModel;
  selectedVehicleAssignment?: VehicleAssignmentModel,
  validateDelegeesPayload?: {
    fileReference: string
  },
  bulkImportProgress?: {
    progress: number;
    hasErrors: boolean;
    numberOfErrors: number;
  },
  vehicleAssigmentsMyFleetPayload?: {
    query?: GetVehicleAssignmentsQuery;
    pageNumber: number;
    totalPages?: number;
    totalRecords?: number;
    payload?: VehicleAssignment[];
  },
  revokeProductsModel?: RevokeVehicleAssignmentsModel,
  assignedVehicleMutation?: AssignedVehicleSuccessfulMutation;
  isBulkImportCompleted?: boolean;
  error?: ErrorDetailsModel;
  calculatedWhitelistDelta?: {
    amountOfVehiclesToUpdate: number;
    amountOfVehiclesToCreate: number;
    amountOfVehiclesToDelete: number;
    amountOfVehiclesWithNoChanges: number;
    hasChanges: boolean;
    fileReference: string;
  },
  isBulkVehicleAssignmentsCompleted?: boolean;
  validatedVehicleBulkUpload?: {
    amountOfVehiclesToCreate: number;
    amountOfExceedingVehicles: number;
    fileReference: string;
    insufficientAvailabilityDetails?: InsufficientAvailabilityDetail[];
  };
  deletedDelegeeIds?: string[];
  deletedVehicleAssignmentIds?: string[];
  revokedParkingRightIds?: string[];
  selectedParkingRightProductPlaceId?: string;
};

export type ParkingProductsAction = StrictParkingProductsAction | ParkingProductsActionType;

const deleteDelegee = (registrationId: string, seasonTicketOwnerCrmId: string, sendRevocationConfirmationEmail: boolean, callback?: () => void) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    dispatch({
      type: request(parkingProductsTypes.DELETE_DELEGEE),
    });

    parkingProductsService
      .deleteDelegee(registrationId, seasonTicketOwnerCrmId, sendRevocationConfirmationEmail)
      .then(() => {
        dispatch({
          type: success(parkingProductsTypes.DELETE_DELEGEE),
          deletedDelegeeIds: [registrationId]
        });

        if (!isUndefined(callback)) {
          callback();
        }

        toastTriggers.success({
          content: {
            key: "parkingRights.deleteDelegee.success",
          },
        });
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        dispatch({
          type: failure(parkingProductsTypes.DELETE_DELEGEE),
          error: error.response?.data as ErrorDetailsModel,
        });
        if  (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
          toastTriggers.error({
          content: {
            key: "errors.invalidSeasonTicketCrmId",
          },
        });
        } else {
          toastTriggers.error({
            content: {
              key: "parkingRights.deleteDelegee.error",
            },
          });
        }
      });
  };
};

const getDelegatedParkingProducts = (
  req: WithFiltersAndPaging<ForFleetManager>
) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    // Get PageId froms select located product card. Needed for parkingRight optimistic update MyProducts table
    const selectedPageId = req.filters?.find(f => f.key == FmpFilters.placeId)?.value as string | undefined;
    dispatch({
      type: request(parkingProductsTypes.GET_DELEGATED_PARKING_RIGHTS),
      delegatedParkingProductsPayload: {
        pageNumber: req.pagination.pageNumber
      },
      selectedParkingRightProductPlaceId: selectedPageId
    });

    parkingProductsService
      .getDelegatedParkingProducts(req)
      .then((response: AxiosResponse<DelegatedParkingRightsWithPagination>) =>
        dispatch({
          type: success(parkingProductsTypes.GET_DELEGATED_PARKING_RIGHTS),
          delegatedParkingProductsPayload: {
            pageNumber: req.pagination.pageNumber,
            totalPages: response.data.pages,
            totalRecords: response.data.totalRecords,
            payload: response.data.delegatedParkingRights,
          },
        })
      )
      .catch((error: AxiosError) =>
        dispatch({
          type: failure(parkingProductsTypes.GET_DELEGATED_PARKING_RIGHTS),
          error: error.response?.data as ErrorDetailsModel | undefined,
        })
      );
  };
};

const clearDelegeeDetails = () => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch({ type: parkingProductsTypes.CLEAR_DELEGEE_DETAILS });
  };
};

const resetBulkImportProgress = () => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch({ type: parkingProductsTypes.RESET_BULK_UPLOAD_PROGRESS });
  };
};

const disposeError = () => {
  return (dispatch: Dispatch<AnyAction>, getState: () => RootReducer) => {
    const { error } = getState().parkingProducts;

    if (!isNull(error)) {
      dispatch({
        type: parkingProductsTypes.DISPOSE_ERROR,
      });
    }
  };
};

const getBulkImportProgress = (
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  pollBulkImportProgress?: () => void
) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    parkingProductsService
      .getBulkImportProgress(seasonTicketOwnerCrmId, fileReference)
      .then((response: AxiosResponse<GetBulkImportProgressResponseModel>) => {
        dispatch({
          type: success(parkingProductsTypes.GET_BULK_IMPORT_PROGRESS),
          bulkImportProgress: {
            progress: response.data.progress,
            hasErrors: response.data.hasErrors,
            numberOfErrors: response.data.numberOfErrors
          }
        });
        if (!isUndefined(pollBulkImportProgress) && response.data.progress !== 100) {
          pollBulkImportProgress();
        }
      })
      .catch((error: AxiosError) => {
        // For testing purposes (until endpoint is implemented)
        // dispatch({
        //   type: success(parkingProductsTypes.GET_BULK_IMPORT_PROGRESS),
        //   bulkImportProgress: progress += 10,
        // });
        dispatch({
          type: failure(parkingProductsTypes.GET_BULK_IMPORT_PROGRESS),
          error: error.response?.data as ErrorDetailsModel | undefined,
        });
      });
  };
};

const getBulkVehicleImportProgress = (
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  pollBulkImportProgress?: () => void
) => {
  return (dispatch: Dispatch<ParkingProductsAction>) => {
    parkingProductsService
      .getBulkVehicleImportProgress(seasonTicketOwnerCrmId, fileReference)
      .then((response: AxiosResponse<GetBulkVehicleImportProgressResponseModel>) => {
        dispatch({
          type: success(strictParkingProductsTypes.GET_BULK_VEHICLE_IMPORT_PROGRESS),
          payload: {
            progress: response.data.progress,
            errorCount: response.data.errorCount,
            warningCount: response.data.warningCount,
          }
        });
        if (!isUndefined(pollBulkImportProgress) && response.data.progress !== 100) {
          pollBulkImportProgress();
        }
      })
      .catch((error: AxiosError) => {
        dispatch({
          type: failure(strictParkingProductsTypes.GET_BULK_VEHICLE_IMPORT_PROGRESS),
          error: error.response?.data as any,
        });
      });
  };
};

const getBulkImportStatus = (seasonTicketOwnerCrmId: string) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    dispatch({
      type: request(parkingProductsTypes.GET_BULK_IMPORT_STATUS)
    });

    return parkingProductsService.getBulkImportStatus(seasonTicketOwnerCrmId).then((response: AxiosResponse<GetBulkImportStatusResponseModel>) => {
      dispatch({
        type: success(parkingProductsTypes.GET_BULK_IMPORT_STATUS),
        isBulkImportCompleted: response.data.processCompleted
      });
    }).catch((error: AxiosError) => {
      dispatch({
        type: failure(parkingProductsTypes.GET_BULK_IMPORT_STATUS),
        error: error.response?.data as ErrorDetailsModel | undefined,
      });
    });
  }
}

const resendDelegeeInvite = (
  registrationId: string,
  seasonTicketOwnerCrmId: string,
  email: string,
  entryRightId: string
) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    dispatch({
      type: request(parkingProductsTypes.RESEND_DELEGEE_INVITE),
    });

    const resendRequest = <ResendDelegeeInviteRequest>{
      email: email,
      registrationId: registrationId,
      seasonTicketOwnerCrmId: seasonTicketOwnerCrmId,
      entryRightId: entryRightId
    }

    parkingProductsService
      .resendDelegeeInvite(registrationId, seasonTicketOwnerCrmId, resendRequest)
      .then(() => {
        dispatch({
          type: success(parkingProductsTypes.RESEND_DELEGEE_INVITE),
        });

        toastTriggers.success({
          content: {
            key: "parkingRights.resendInvite.success",
          },
        });
      })
      .catch((error: AxiosError) => {
        dispatch({
          type: failure(parkingProductsTypes.RESEND_DELEGEE_INVITE),
          error: error.response?.data as ErrorDetailsModel | undefined,
        });

        if(error.response?.status == 403){
           toastTriggers.error({
            content: {
              key: "parkingRights.resendInvite.rateLimitError",
            },
          });
        } else if (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
          toastTriggers.error({
            content: {
              key: "errors.invalidSeasonTicketCrmId",
            },
          });
        } else{
          toastTriggers.error({
            content: {
              key: "parkingRights.resendInvite.error",
            },
          });
      }
      });
  };
};

const revokeParkingRights = (
  model: RevokeParkingRightsModel,
  callback?: () => void
) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    dispatch({
      type: request(parkingProductsTypes.REVOKE_PARKINGRIGHTS),
    });

    parkingProductsService
      .revokeParkingRights(model)
      .then(() => {
        dispatch({
          type: success(parkingProductsTypes.REVOKE_PARKINGRIGHTS),
          revokedParkingRightIds: model.parkingRights.map(pr => pr.parkingRightId)
        });

        if (!isUndefined(callback)) {
          callback();
        }

        toastTriggers.success({
          content: {
            key: "parkingRights.revokeParkingRights.success",
            options: { count: model.parkingRights.length },
          },
        });
      })
      .catch((error: AxiosError) => {
        dispatch({
          type: failure(parkingProductsTypes.REVOKE_PARKINGRIGHTS),
          error: error.response?.data as ErrorDetailsModel,
        });

        if (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
          toastTriggers.error({
            content: {
              key: "errors.invalidSeasonTicketCrmId",
            },
          });
        } else {
          toastTriggers.error({
            content: {
              key: "parkingRights.revokeParkingRights.error",
            },
          });
        }

      });
  };
};

const deleteDelegations = (
  model: DeleteDelegationsRequestModel,
  callback?: () => void
) => {
  return (dispatch: Dispatch<any>) => {
    dispatch({
      type: request(parkingProductsTypes.DELETE_DELEGEES),
    });

    dispatch(loaderActions.loadTheLoader());

    parkingProductsService
      .deleteDelegations(model)
      .then(() => {
        dispatch({
          type: success(parkingProductsTypes.DELETE_DELEGEES),
          deletedDelegeeIds: model.registrationIds
        });

        if (!isUndefined(callback)) {
          callback();
        }

        toastTriggers.success({
          content: {
            key: "parkingRights.deleteDelegee.success_plural",
            options: { count: model.registrationIds.length },
          },
        });
      })
      .catch((error: AxiosError) => {
        dispatch({
          type: failure(parkingProductsTypes.DELETE_DELEGEES),
          error: error.response?.data as ErrorDetailsModel,
        });

        if (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
          toastTriggers.error({
            content: {
              key: "errors.invalidSeasonTicketCrmId",
            },
          });
        } else {
          toastTriggers.error({
            content: {
              key: "parkingRights.deleteDelegee.error_plural",
            },
          });
        }
      })
      .finally(() => dispatch(loaderActions.killTheLoader()));
  };
};

const validateDelegees = (
  seasonTicketOwnerCrmId: string,
  req: ValidateDelegeesRequestModel,
  onSuccess?: (response: ValidateDelegeesResponseModel) => void,
  onError?: (errorCode: string) => void
) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    const requestModel = {
      ...req,
      products: setTodayActivationDatesToNowIfToday(req.products),
    };

    dispatch({
      type: request(parkingProductsTypes.VALIDATE_DELEGEES),
    });

    parkingProductsService
      .validateDelegees(seasonTicketOwnerCrmId, requestModel)
      .then(({ data }) => {
        dispatch({
          type: success(parkingProductsTypes.VALIDATE_DELEGEES),
          validateDelegeesPayload: {
            fileReference: data.fileReference
          }
        });
        if (!isUndefined(onSuccess)) {
          onSuccess(data);
        }
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        dispatch({
          type: failure(parkingProductsTypes.VALIDATE_DELEGEES),
        });

        const errorCode = getFirstErrorCode(error);

        toastTriggers.error({
          content: {
            key: "parkingRights.inviteDelegee.error",
          },
        });

        if (!isUndefined(onError)) {
          onError(errorCode);
        }
      });
  };
};

const startBulkInvitations = (
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  req: StartBulkInvitationsRequestModel,
  onSuccess?: () => void,
  onError?: (errorCode: string) => void
) => {
  return (dispatch: Dispatch<any>) => {
    dispatch({
      type: request(parkingProductsTypes.IMPORT_DELEGEES),
    });

    parkingProductsService
      .startBulkInvitations(seasonTicketOwnerCrmId, fileReference, req)
      .then(() => {
        dispatch({
          type: success(parkingProductsTypes.IMPORT_DELEGEES),
        });
        if (!isUndefined(onSuccess)) {
          onSuccess();
        }
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        dispatch({
          type: failure(parkingProductsTypes.IMPORT_DELEGEES),
        });

        const errorCode = getFirstErrorCode(error);

        if (!isUndefined(onError)) {
          onError(errorCode);
        }
      });
  };
}

const startBulkVehicleAssignment = (
  seasonTicketOwnerCrmId: string,
  fileReference: string,
  startBulkVehicleAssignmentRequest: StartBulkVehicleAssignmentRequest,
  onSuccess?: () => void,
  onError?: (errorCode: string) => void
) => {
  return (dispatch: Dispatch<ParkingProductsAction>) => {
    dispatch({
      type: request(strictParkingProductsTypes.START_BULK_VEHICLE_ASSIGNMENT),
    });

    parkingProductsService
      .startBulkVehicleAssignment(seasonTicketOwnerCrmId, fileReference, startBulkVehicleAssignmentRequest)
      .then(() => {
        dispatch({
          type: success(strictParkingProductsTypes.START_BULK_VEHICLE_ASSIGNMENT),
        });
        if (!isUndefined(onSuccess)) {
          onSuccess();
        }
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        dispatch({
          type: failure(strictParkingProductsTypes.START_BULK_VEHICLE_ASSIGNMENT),
        });

        const errorCode = getFirstErrorCode(error);

        if (!isUndefined(onError)) {
          onError(errorCode);
        }
      });
  };
}

const assignVehicle = (
  seasonTicketOwnerCrmId: string,
  req: AssignVehicleRequestModel,
  onSuccess?: () => void,
  onError?: (errorCode: string) => void
) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    const requestModel = {
      ...req,
      parkingProducts: setTodayActivationDatesToNowIfToday(req.parkingProducts),
    };

    dispatch({
      type: request(parkingProductsTypes.ASSIGN_VEHICLE),
    });

    parkingProductsService
      .assignVehicle(seasonTicketOwnerCrmId, requestModel)
      .then((response) => {
        dispatch({
          type: success(parkingProductsTypes.ASSIGN_VEHICLE),
          assignedVehicleMutation: {
            type: 'assign',
            request: requestModel,
            pmcToParkingRightStartDates: response.data.pmcToParkingRight
          }
        });
        if (!isUndefined(onSuccess)) {
          onSuccess();
        }
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        checkForCrmIdOrLinkedAccessDevice(
          dispatch,
          error.response?.data as ErrorDetailsModel | null
        );

        dispatch({
          type: failure(parkingProductsTypes.ASSIGN_VEHICLE),
          error: error.response?.data as ErrorDetailsModel,
        });

        const errorCode = getFirstErrorCode(error);

        if (!isUndefined(onError)) {
          onError(errorCode);
        }
      });
  };
}
const updateVehicleAssignment = (
  seasonTicketOwnerCrmId: string,
  aggregateId: string,
  req: UpdateVehicleRequestModel,
  onSuccess?: () => void) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    const requestModel = {
      ...req,
      parkingProducts: setTodayActivationDatesToNowIfToday(req.parkingProducts),
    };

    dispatch({
      type: request(parkingProductsTypes.UPDATE_VEHICLE_ASSIGNMENT),
    });

    parkingProductsService
      .updateVehicleAssignment(seasonTicketOwnerCrmId, aggregateId, requestModel)
      .then((response) => {
        dispatch({
          type: success(parkingProductsTypes.UPDATE_VEHICLE_ASSIGNMENT),
          assignedVehicleMutation: {
            type: 'update',
            aggregateId,
            request: requestModel,
            pmcToParkingRightStartDates: response.data.pmcToParkingRight
          }
        });
        if (!isUndefined(onSuccess)) {
          onSuccess();
        }
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        dispatch({
          type: failure(parkingProductsTypes.UPDATE_VEHICLE_ASSIGNMENT),
          error: error.response?.data as ErrorDetailsModel,
        });

        if (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
          toastTriggers.error({
            content: {
              key: "errors.invalidSeasonTicketCrmId",
            },
          });
        } else {
          toastTriggers.error({
            content: {
              key: "parkingRights.inviteDelegee.error",
            },
          });
        }
      });
  };
}

const getVehicleAssignment = (
  aggregateId: string, onSuccess?: (res: VehicleAssignmentModel) => void) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    dispatch({
      type: request(parkingProductsTypes.GET_ASSIGNED_VEHICLE),
    });

    parkingProductsService.getVehicleAssignment(aggregateId)
      .then((response) => {
        dispatch({
          type: success(parkingProductsTypes.GET_ASSIGNED_VEHICLE),
          selectedVehicleAssignment: response.data
        });
        if (!isUndefined(onSuccess)) {
          onSuccess(response.data);
        }
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        dispatch({
          type: failure(parkingProductsTypes.GET_ASSIGNED_VEHICLE),
          error: error.response?.data as ErrorDetailsModel,
        });
      });
  };
}

const getVehicleAssignmentsMyFleet = (req: WithFiltersAndPaging<ForFleetManager>, excludedIds: string[] = []) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    const query = convertFiltersToGetVehicleAssignmentsQuery(req);
    const originalPageSize = req.pagination.pageSize;
    // overfetch to fill the page in case deleted items are still in the projection
    query.pageSize += excludedIds.length;
    dispatch({
      type: request(parkingProductsTypes.GET_VEHICLE_ASSIGNMENTS_MY_FLEET),
      vehicleAssigmentsMyFleetPayload: {
        pageNumber: req.pagination.pageNumber,
        query
      }
    });

    parkingProductsService.getVehicleAssignmentsMyFleet(query)
      .then((response) => {
        const toRemove = response.data.vehicleAssignments.filter(x => excludedIds.includes(x.vehicleAssignmentId));
        const vehicle = response.data.vehicleAssignments.filter(x => !toRemove.includes(x)).slice(0, originalPageSize);

        dispatch({
          type: success(parkingProductsTypes.GET_VEHICLE_ASSIGNMENTS_MY_FLEET),
          vehicleAssigmentsMyFleetPayload: {
            pageNumber: req.pagination.pageNumber,
            totalPages: response.data.pages,
            totalRecords: response.data.totalRecords - toRemove.length,
            payload: vehicle
          }
        });
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        dispatch({
          type: failure(parkingProductsTypes.GET_VEHICLE_ASSIGNMENTS_MY_FLEET),
          error: error.response?.data as ErrorDetailsModel,
        });
      });
  };
}

const getFirstErrorCode = (error: AxiosError<ErrorDetailsModel>) =>{
    let errorCode = error.message;
    const errors = error.response?.data.errors;
    const fields = error.response?.data.fields;

    if (errors !== undefined && errors.length > 0 &&
        fields !== undefined && fields.length > 0) {
        errorCode = Object.values(errors)[0][fields[0]];
    }

    return errorCode;
}

const revokeVehicleAssignmentProducts = (
  model: RevokeVehicleAssignmentsModel,
  callback?: () => void
) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    dispatch({
      type: request(parkingProductsTypes.REVOKE_VEHICLE_ASSIGNMENTS),
      revokeProductsModel: model
    });

    parkingProductsService
      .revokeVehicleAssignments(model)
      .then(() => {
        dispatch({
          type: success(parkingProductsTypes.REVOKE_VEHICLE_ASSIGNMENTS),
          revokeProductsModel: model
        });

        if (!isUndefined(callback)) {
          callback();
        }

        toastTriggers.success({
          content: {
            key: "parkingRights.revokeVehicleAssignment.success",
            options: { count: model.vehicleAssignmentProducts.length },
          },
        });
      })
      .catch((error: AxiosError) => {
        dispatch({
          type: failure(parkingProductsTypes.REVOKE_VEHICLE_ASSIGNMENTS),
          error: error.response?.data as ErrorDetailsModel,
        });

        if (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
          toastTriggers.error({
            content: {
              key: "errors.invalidSeasonTicketCrmId",
            },
          });
        } else {
          toastTriggers.error({
            content: {
              key: "parkingRights.revokeVehicleAssignment.error",
            },
          });
        }
      });
  };
};

const deleteVehicleAssignments = (
  model: DeleteVehicleAssignmentsModel,
  callback?: () => void
) => {
  return (dispatch: Dispatch<ParkingProductsActionType>) => {
    dispatch({
      type: request(parkingProductsTypes.DELETE_VEHICLE_ASSIGNMENTS),
    });

    parkingProductsService
      .deleteVehicleAssignments(model)
      .then(() => {
        dispatch({
          type: success(parkingProductsTypes.DELETE_VEHICLE_ASSIGNMENTS),
          deletedVehicleAssignmentIds: model.vehicleAssignmentIds,
        });

        if (!isUndefined(callback)) {
          callback();
        }

        toastTriggers.success({
          content: {
            key: "parkingRights.deleteVehicleAssignment.success",
            options: { count: model.vehicleAssignmentIds.length },
          },
        });
      })
      .catch((error: AxiosError) => {
        dispatch({
          type: failure(parkingProductsTypes.DELETE_VEHICLE_ASSIGNMENTS),
          error: error.response?.data as ErrorDetailsModel,
        });

        if (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
          toastTriggers.error({
            content: {
              key: "errors.invalidSeasonTicketCrmId",
            },
          });
        } else {
          toastTriggers.error({
            content: {
              key: "parkingRights.deleteVehicleAssignment.error",
            },
          });
        }
      });
  };
};

const calculateWhitelistDelta = (
  req: CalculateWhitelistDeltaRequest,
  onSuccess?: (response: CalculatedWhitelistDeltaResponse) => void,
  onError?: (errorCode: string) => void
) => {
  return (dispatch: Dispatch<ParkingProductsAction>) => {
    dispatch({
      type: request(strictParkingProductsTypes.CALCULATE_WHITELIST_DELTA),
    });
    parkingProductsService
      .calculateWhitelistDelta(req)
      .then(({ data }) => {
        dispatch({
          type: success(strictParkingProductsTypes.CALCULATE_WHITELIST_DELTA),
          payload: {
            amountOfVehiclesToCreate: data.amountOfVehiclesToCreate,
            amountOfVehiclesToDelete: data.amountOfVehiclesToDelete,
            amountOfVehiclesToUpdate: data.amountOfVehiclesToUpdate,
            amountOfVehiclesWithNoChanges: data.amountOfVehiclesWithNoChanges,
            hasChanges: data.hasChanges,
            fileReference: data.fileReference
          }
        });
        if (!isUndefined(onSuccess)) {
          onSuccess(data);
        }
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        dispatch({
          type: failure(strictParkingProductsTypes.CALCULATE_WHITELIST_DELTA),
          error: error.response?.data as ErrorDetailsModel,
        });

        const errorCode = getFirstErrorCode(error);

        if (!isUndefined(onError)) {
          onError(errorCode);
        }
      });
  };
};

const getBulkVehicleAssignmentsStatus = (seasonTicketOwnerCrmId: string) => {
    return (dispatch: Dispatch<ParkingProductsAction>) => {
        dispatch({
            type: request(strictParkingProductsTypes.GET_BULK_VEHICLE_ASSIGNMENTS_STATUS)
        });

        parkingProductsService.getBulkVehicleAsignmentsStatus(seasonTicketOwnerCrmId).then((response: AxiosResponse<GetBulkVehicleAssignmentsStatusResponseModel>) => {
            dispatch({
                type: success(strictParkingProductsTypes.GET_BULK_VEHICLE_ASSIGNMENTS_STATUS),
                payload: {
                  isCompleted: response.data.processCompleted
                }
            });
        }).catch((error: AxiosError) => {
            dispatch({
                type: failure(strictParkingProductsTypes.GET_BULK_VEHICLE_ASSIGNMENTS_STATUS),
                error: error.response?.data as ErrorDetailsModel | undefined,
            });
        });
    }
}

const resetBulkVehicleImportProgress = () => {
  return { type: strictParkingProductsTypes.RESET_BULK_VEHICLE_IMPORT_PROGRESS };
}

const validateVehicleBulkUpload = (
  req: ValidateVehicleBulkUploadRequest,
  onSuccess?: (response: ValidatedVehicleBulkUploadResponse) => void,
  onError?: (errorCode: string) => void
) => {
  return (dispatch: Dispatch<ParkingProductsAction>) => {
    dispatch({
      type: request(strictParkingProductsTypes.VALIDATE_VEHICLE_BULK_UPLOAD),
    });
    parkingProductsService
      .validateVehicleBulkUpload(req)
      .then(({ data }) => {
        dispatch({
          type: success(strictParkingProductsTypes.VALIDATE_VEHICLE_BULK_UPLOAD),
          payload: {
            amountOfVehiclesToCreate: data.amountOfVehiclesToCreate,
            amountOfExceedingVehicles: data.amountOfExceedingVehicles,
            fileReference: data.fileReference,
            insufficientAvailabilityDetails: data.insufficientAvailabilityDetails,
          }
        });
        if (!isUndefined(onSuccess)) {
          onSuccess(data);
        }
      })
      .catch((error: AxiosError<ErrorDetailsModel>) => {
        dispatch({
          type: failure(strictParkingProductsTypes.VALIDATE_VEHICLE_BULK_UPLOAD),
          error: error.response?.data,
        });

        if (checkForSeasonTicketCrmIdNotValidError(error.response?.data as ErrorDetailsModel | null)) {
          toastTriggers.error({
            content: {
              key: "errors.invalidSeasonTicketCrmId",
            },
          });
        }

        const errorCode = getFirstErrorCode(error);

        if (!isUndefined(onError)) {
          onError(errorCode);
        }
      });
  };
};

export default {
  getDelegatedParkingProducts,
  disposeError,
  clearDelegeeDetails,
  deleteDelegee,
  resendDelegeeInvite,
  revokeParkingRights,
  deleteDelegations,
  validateDelegees,
  startBulkInvitations,
  getBulkImportProgress,
  getBulkImportStatus,
  getBulkVehicleAssignmentsStatus,
  resetBulkImportProgress,
  assignVehicle,
  updateVehicleAssignment,
  getVehicleAssignment,
  revokeVehicleAssignmentProducts,
  deleteVehicleAssignments,
  getVehicleAssignmentsMyFleet,
  calculateWhitelistDelta,
  startBulkVehicleAssignment,
  getBulkVehicleImportProgress,
  resetBulkVehicleImportProgress,
  validateVehicleBulkUpload
};
